import React from 'react';

import type { CaraWorkspaceFileInput } from '~/api/graphql';

/**
 * State for one "side" (prefix or suffix PDF) of the HTML template's
 * Start-Pages / End-Pages file fields. Each editor instantiates two of these,
 * one for prefix and one for suffix.
 *
 * `files` is the user's pending pick (new uploads or references to existing
 * library files); `reset` is the "user wants to clear the existing file even
 * though they haven't picked a replacement" flag. Together they determine
 * how the submit step resolves the prefix/suffix file id and upload.
 *
 * `onChange` is fired on every state-mutating call so callers can react to
 * pending prefix/suffix file changes.
 */
export function usePrefixSuffixFiles(params: { onChange?: () => void }) {
  const { onChange } = params;
  const [files, setFiles] = React.useState<CaraWorkspaceFileInput[]>([]);
  const [reset, setReset] = React.useState(false);

  const pickFiles = React.useCallback(
    (next: CaraWorkspaceFileInput[]) => {
      setFiles(next);
      onChange?.();
    },
    [onChange]
  );

  const resetExisting = React.useCallback(() => {
    setReset(true);
    onChange?.();
  }, [onChange]);

  /** Drops a pick and a pending removal alike, so the stored file stays as it was saved. */
  const discardPending = React.useCallback(() => {
    setFiles([]);
    setReset(false);
    onChange?.();
  }, [onChange]);

  /**
   * Resolve the file id to submit alongside the form. Returns the supplied
   * `currentFileId` when the user has neither picked a new file nor reset
   * the existing one (the existing file is kept); returns `null` otherwise
   * (the existing file is cleared, optionally replaced by an upload).
   */
  const resolveFileId = React.useCallback(
    (currentFileId: string | null | undefined): string | null =>
      files.length === 0 && !reset ? (currentFileId ?? null) : null,
    [files, reset]
  );

  /** The newly-uploaded `File`, if the user picked one (vs. an existing reference). */
  const getUpload = React.useCallback((): File | null => {
    const first = files[0];
    if (!first || !('upload' in first) || !first.upload) return null;
    return first.upload;
  }, [files]);

  return {
    files,
    reset,
    pickFiles,
    resetExisting,
    discardPending,
    resolveFileId,
    getUpload
  };
}

export type UsePrefixSuffixFilesResult = ReturnType<typeof usePrefixSuffixFiles>;
